import { c as _c } from "react/compiler-runtime";
import type { Option } from "@repo/design-system/types/filtermenu";
import { cn } from "@repo/design-system/utils/cn";
import { ChevronDown, X } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { Badge } from "~/components/ui/badge-eon";
import { Button } from "~/components/ui/button-eon";
export function SelectLayout(t0) {
  const $ = _c(20);
  let children;
  let className;
  let hasValue;
  let label;
  let open;
  let props;
  let textColor;
  if ($[0] !== t0) {
    ({
      open,
      label,
      children,
      hasValue,
      className,
      textColor,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = hasValue;
    $[4] = label;
    $[5] = open;
    $[6] = props;
    $[7] = textColor;
  } else {
    children = $[1];
    className = $[2];
    hasValue = $[3];
    label = $[4];
    open = $[5];
    props = $[6];
    textColor = $[7];
  }
  let t1;
  if ($[8] !== className) {
    t1 = cn("relative flex w-full items-center justify-between border bg-transparent", className);
    $[8] = className;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const t2 = hasValue ?? false;
  let t3;
  if ($[10] !== label || $[11] !== t2 || $[12] !== textColor) {
    t3 = <FloatingLabel label={label} open={t2} textColor={textColor} />;
    $[10] = label;
    $[11] = t2;
    $[12] = textColor;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== children || $[15] !== open || $[16] !== props || $[17] !== t1 || $[18] !== t3) {
    t4 = <Button aria-expanded={open} className={t1} {...props}>{t3}{children}</Button>;
    $[14] = children;
    $[15] = open;
    $[16] = props;
    $[17] = t1;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
}
function FloatingLabel(t0) {
  const $ = _c(6);
  const {
    label,
    open,
    textColor
  } = t0;
  const t1 = open && "absolute translate-x-0 -translate-y-3 rounded-lg bg-brand text-base";
  const t2 = textColor || "text-secondary-foreground";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cn("top-0 left-2 origin-[0] transform rounded-sm bg-transparent px-2 transition-all duration-200", t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== label || $[4] !== t3) {
    t4 = <span className={t3}>{label}</span>;
    $[3] = label;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
export function OptionDisplay(t0) {
  const $ = _c(4);
  const {
    activeFilters
  } = t0;
  let t1;
  if ($[0] !== activeFilters.length) {
    t1 = activeFilters.length > 0 && <Badge className="mr-1 mb-1 rounded-none bg-transparent">+{activeFilters.length}</Badge>;
    $[0] = activeFilters.length;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="flex h-10 flex-wrap items-center overflow-hidden">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export function Clear(t0) {
  const $ = _c(7);
  const {
    onChange,
    hasValue
  } = t0;
  if (!hasValue) {
    return null;
  }
  let t1;
  let t2;
  if ($[0] !== onChange) {
    t1 = e => {
      e.stopPropagation();
      onChange();
    };
    t2 = e_0 => {
      e_0.stopPropagation();
      onChange();
    };
    $[0] = onChange;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <X className="h-4 w-4" color="currentColor" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t2) {
    t4 = <slot aria-label="Clear all" className="rounded-full p-1 hover:bg-gray-100" onClick={t1} onKeyDown={t2} role="button">{t3}</slot>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
export function SelectIndicator(t0) {
  const $ = _c(7);
  const {
    open,
    children
  } = t0;
  const t1 = open && "rotate-180";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("h-5 w-5 shrink-0 opacity-90 transition-transform duration-200", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = <ChevronDown className={t2} color="currentColor" />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children || $[5] !== t3) {
    t4 = <div className="mt-0.5 ml-2 flex items-center gap-2">{children}{t3}</div>;
    $[4] = children;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}